import React, {useState} from 'react';
import './tmdbCollectionSelector.scss';
import {cn} from "../../../../utils/utils";
import {useSelector} from "react-redux";
import users from "../../../users";
import CustomModalV2 from "../../../shared/custom-modal-v2/custom-modal-v2";
import {SquadflixCollectionSummary} from "../../../app/myProfileView/my-profile-subroutes/my-profile-lists/types";
import {toast} from "react-toastify";
import {Spinner} from "../../../shared";
import SquadflixCollectionForm from "../../squadflix-collection-form/squadflix-collection-form";
import {Link} from "react-router-dom";
import ButtonV2 from "../../../shared/button-v2/button-v2";
import {SquadflixCollectionsApi} from "../../../../apis/squadflix-collections/SquadflixCollectionsApi";

type Props = {
    item?: any;
    isMovie?: boolean;
    onSelected?: (collection: SquadflixCollectionSummary) => void;
    customBtn?: React.ReactNode;
    actionText?: string;
    disabledCollections?: string[];
}

const TmdbCollectionSelector = (props: Props) => {

    const {
        actionText = "Agregar a ",
        disabledCollections = []
    } = props;

    const darkMode = useSelector(users.selectors.isDarkMode);
    const [loading, setLoading] = useState(false);
    const [loadingCollections, setLoadingCollections] = useState(false);
    const [open, setOpen] = useState(false);
    const [collections, setCollections] = useState<SquadflixCollectionSummary[]>([]);
    const [selectedItem, setSelectedItem] = useState<SquadflixCollectionSummary | null>(null);
    const [createNewCollectionOpen, setCreateNewCollectionOpen] = useState(false);

    const handleOpenModal = () => {
        setOpen(true);
        setCollections([]);
        setLoadingCollections(true);
        SquadflixCollectionsApi.getMyCollectionsSummary().then(res => {
            setCollections(res.collections ?? []);
        }).catch(e => {
            toast.error("Error al cargar las colecciones");
        }).finally(() => {
            setLoadingCollections(false);
        });
    }

    const handleCloseModal = () => {
        if (loading) return;
        setOpen(false);
        setSelectedItem(null);
    }

    const handleAddToCollection = () => {
        if (!selectedItem) return;
        if (!props.item) {
            if (props.onSelected) {
                props.onSelected(selectedItem);
                handleCloseModal();
            }
            return;
        }
        setLoading(true);
        SquadflixCollectionsApi.addItemToCollection({
            collectionId: selectedItem.id,
            isMovie: props.isMovie,
            tmdbId: props.item.id,
            episode: -1,
            season: -1,
        }).then(res => {
            if (res.success) {
                toast.success("Ítem agregado a la colección");
                handleCloseModal();
            } else {
                toast.error("Error al agregar el ítem a la colección");
            }
        }).catch(e => {
            try {
                const error: { error: string } = JSON.parse(e.message);
                toast.error(error.error);
            } catch {
                toast.error("Error al agregar el ítem a la colección");
            }
        }).finally(() => {
            setLoading(false);
        });
    }

    return (
        <>
            {props.customBtn ?
                <ButtonV2
                    width="fit-content"
                    disabled={loading}
                    onClick={handleOpenModal}>
                    {!loading && props.customBtn}
                    {loading && <i className="fa-solid fa-spinner fa-spin fa-lg"></i>}
                </ButtonV2>
                : <ButtonV2
                    width={50}
                    disabled={loading}
                    title={actionText.trim() + " colección"}
                    onClick={handleOpenModal}>
                    {!loading && <i className="fa-regular fa-bookmark fa-lg"></i>}
                    {loading && <i className="fa-solid fa-spinner fa-spin fa-lg"></i>}
                </ButtonV2>}
            <SquadflixCollectionForm
                open={createNewCollectionOpen}
                setOpen={(isOpen) => {
                    if (!isOpen) {
                        setOpen(true);
                    }
                    setCreateNewCollectionOpen(isOpen);
                }}
                onCreate={(collection) => {
                    setCollections(prev => [collection, ...prev]);
                    setSelectedItem(collection);
                    setOpen(true);
                }}/>
            <CustomModalV2
                title={actionText.trim() + " colección"} open={open}
                primaryButton={selectedItem ? {
                    text: loading ? "Agregando..." : actionText.trim() + " " + selectedItem.name,
                    variant: "primary",
                    disabled: loading,
                    onClick: handleAddToCollection
                } : {
                    text: "Selecciona una colección",
                    variant: "primary",
                    disabled: true,
                    onClick: () => {
                    }
                }}
                secondaryButton={{
                    text: "Cancelar",
                    variant: "secondary",
                    onClick: () => {
                        handleCloseModal();
                    }
                }}
                onClose={handleCloseModal}>
                <div className={cn("tmdb-collection-selector", {"tmdb-collection-selector-dark": darkMode})}>
                    {loadingCollections &&
                        <div className="d-flex justify-content-center align-items-center" style={{height: 70}}>
                            <Spinner/>
                        </div>}
                    {!loadingCollections && (
                        <div className="tmdb-collection-selector-collections-list">
                            {collections.map(collection => (
                                <ButtonV2 key={collection.id}
                                          disabled={loading || disabledCollections.includes(collection.id + "")}
                                          className={cn("tmdb-collection-selector-collections-list__item")}
                                          active={selectedItem?.id === collection.id}
                                          onClick={() => {
                                              if (selectedItem?.id === collection.id) {
                                                  setSelectedItem(null);
                                                  return;
                                              }
                                              setSelectedItem(collection);
                                          }}>
                                    <div className="tmdb-collection-selector-collections-list__item-name">
                                        {collection.name}{collection.private &&
                                        <i className="fa-solid fa-lock fa-sm ps-2"></i>}
                                    </div>
                                    <div className="text-muted">
                                        {collection.totalItems} cosas
                                    </div>
                                </ButtonV2>
                            ))}
                            {!loadingCollections && !collections.length && (
                                <div className="tmdb-collection-selector-no-collections mb-4 text-body-emphasis">
                                    No tienes colecciones. Crealas desde <Link className="text-body-emphasis"
                                                                               to="/my-profile/collections">"Mi
                                    Perfil"</Link> o pulsando en el siguiente botón:
                                </div>
                            )}
                            <ButtonV2 disabled={loading}
                                      className={cn("tmdb-collection-selector-collections-list__item")}
                                      onClick={() => {
                                          setCreateNewCollectionOpen(true);
                                          setOpen(false);
                                      }}>
                                <div className="tmdb-collection-selector-collections-list__item-name">
                                    Crear nueva colección
                                    <i className="fa-solid fa-plus fa-sm ps-2"></i>
                                </div>
                            </ButtonV2>
                        </div>
                    )}
                </div>
            </CustomModalV2>
        </>
    )

}

export default TmdbCollectionSelector;
